import React, { useState, useEffect, Fragment, useRef } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
import AppPagination from "../Common/AppPagination";
import { Row, Col, Card, Table, Badge, Dropdown, Modal, Button } from "react-bootstrap";
import api, { getFileUrl as getApiFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import swal from "sweetalert";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import PermissionWrapper from "../PermissionWrapper";

const TCList = ({ workOrderId: propWorkOrderId, isTab = false }) => {
  const user = useSelector((state) => state.auth?.userDetails || {});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialProject = searchParams.get("project") || "";
  const initialWorkOrder = propWorkOrderId || searchParams.get("workOrder") || "";
  const debounceRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [projects, setProjects] = useState([]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  });

  const [filters, setFilters] = usePersistentFilters("tc_list_filters", {
    search: "",
    project: initialProject,
    workOrder: initialWorkOrder,
    docCategory: "",
    status: "",
    fromDate: "",
    toDate: "",
    page: 1,
    limit: 10,
  });

  const getListQueryString = () => {
    const params = new URLSearchParams();
    if (filters.workOrder) params.set("workOrder", filters.workOrder);
    return params.toString() ? `?${params.toString()}` : "";
  };

  useEffect(() => {
    fetchProjects(initialProject);
  }, [initialProject]);

  useEffect(() => {
    const projectFromParams = searchParams.get("project") || "";
    const workOrderFromParams = propWorkOrderId || searchParams.get("workOrder") || "";

    setFilters((prev) => ({
      ...prev,
      project: projectFromParams,
      workOrder: workOrderFromParams,
      page: 1,
    }));
  }, [searchParams, propWorkOrderId]);

  const fetchProjects = async (projectId = "") => {
    try {
      const params = { limit: 100 };
      if (projectId) params.project = projectId;

      const res = await api.get("/work-orders", { params });
      if (res.data.success) {
        setProjects(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to load projects", err);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchDocuments();
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [
    filters.search,
    filters.project,
    filters.workOrder,
    filters.docCategory,
    filters.status,
    filters.fromDate,
    filters.toDate,
    filters.page,
  ]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = {
        type: "TC",
        search: filters.search,
        project: filters.project,
        workOrder: filters.workOrder,
        docCategory: filters.docCategory,
        status: filters.status,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        page: filters.page,
        limit: filters.limit,
      };

      const response = await api.get("/document-control", { params });

      if (response.data.success) {
        setDocuments(response.data.data || []);
        setPagination(response.data.pagination);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error("Error fetching TC documents:", error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await swal({
      title: "Delete TC/Warranty Card?",
      text: "Are you sure you want to delete this TC/Warranty Card record?",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });

    if (!confirmed) return;

    try {
      const response = await api.delete(`/document-control/${id}`);
      if (response.data.success) {
        swal("Deleted!", "TC/Warranty Card deleted successfully", "success");
        fetchDocuments();
      }
    } catch (error) {
      swal("Error", "Failed to delete record", "error");
    }
  };

  const getFileUrl = (file) => {
    if (!file) return "";
    const url = typeof file === "string" ? file : file.fileUrl;
    return getApiFileUrl(url);
  };

  const handleDocumentDelete = async (docId, fileIndex) => {
    const confirmed = await swal({
      title: "Delete document?",
      text: "This attached document will be removed.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });

    if (!confirmed) return;

    try {
      const response = await api.delete(`/document-control/${docId}/documents/${fileIndex}`);
      if (response.data.success) {
        setSelectedDoc(response.data.data);
        setDocuments((prev) =>
          prev.map((item) => (item._id === docId ? response.data.data : item)),
        );
        swal("Deleted!", "Document removed successfully", "success");
      }
    } catch (error) {
      swal("Error", error.response?.data?.message || "Failed to delete document", "error");
    }
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Approved":
        return "success";
      case "Pending Approval":
        return "warning";
      case "Rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const svgMenu = (
    <svg width="20px" height="20px" viewBox="0 0 24 24">
      <circle fill="#000" cx="5" cy="12" r="2"></circle>
      <circle fill="#000" cx="12" cy="12" r="2"></circle>
      <circle fill="#000" cx="19" cy="12" r="2"></circle>
    </svg>
  );

  return (
    <Fragment>
      {!isTab && <PageTitle activeMenu="TC/Warranty Cards" motherMenu="Document Control" />}

      {!isTab && (
        <Row className="mb-3">
          <Col lg={12}>
            <Card className="shadow-sm">
              <Card.Body className="py-3 px-3">
                <div className="d-flex flex-nowrap gap-2 align-items-end">
                  <div style={{ flex: "1.2 1 140px", minWidth: "120px" }}>
                    <label className="form-label font-w600 mb-1 small text-nowrap">Search Product / WLIN</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ height: "36px" }}
                      placeholder="Search..."
                      value={filters.search}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          search: e.target.value,
                          page: 1,
                        })
                      }
                    />
                  </div>

                  <div style={{ flex: "1.4 1 160px", minWidth: "140px" }}>
                    <label className="form-label font-w600 mb-1 small text-nowrap">Work Order</label>
                    <select
                      className="form-select form-select-sm"
                      style={{ height: "36px" }}
                      value={filters.workOrder}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          project:
                            projects.find((wo) => wo._id === e.target.value)
                              ?.project?._id || filters.project,
                          workOrder: e.target.value,
                          page: 1,
                        })
                      }
                    >
                      <option value="">All Work Orders</option>
                      {projects.map((wo) => (
                        <option key={wo._id} value={wo._id}>
                          {wo.woNo} - {wo.project?.projectName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ flex: "0.9 1 110px", minWidth: "100px" }}>
                    <label className="form-label font-w600 mb-1 small text-nowrap">Doc Type</label>
                    <select
                      className="form-select form-select-sm"
                      style={{ height: "36px" }}
                      value={filters.docCategory}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          docCategory: e.target.value,
                          page: 1,
                        })
                      }
                    >
                      <option value="">All Types</option>
                      <option value="TC">Test Certificate (TC)</option>
                      <option value="Warranty Card">Warranty Card</option>
                    </select>
                  </div>

                  <div style={{ flex: "0.8 1 100px", minWidth: "95px" }}>
                    <label className="form-label font-w600 mb-1 small text-nowrap">Status</label>
                    <select
                      className="form-select form-select-sm"
                      style={{ height: "36px" }}
                      value={filters.status}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          status: e.target.value,
                          page: 1,
                        })
                      }
                    >
                      <option value="">All Status</option>
                      <option value="Approved">Approved</option>
                      <option value="Pending Approval">Pending Approval</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  </div>

                  <div style={{ width: "120px", flexShrink: 0 }}>
                    <label className="form-label font-w600 mb-1 small text-nowrap">From Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ height: "36px" }}
                      value={filters.fromDate}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          fromDate: e.target.value,
                          page: 1,
                        })
                      }
                    />
                  </div>

                  <div style={{ width: "120px", flexShrink: 0 }}>
                    <label className="form-label font-w600 mb-1 small text-nowrap">To Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ height: "36px" }}
                      value={filters.toDate}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          toDate: e.target.value,
                          page: 1,
                        })
                      }
                    />
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    <PermissionWrapper user={user} required={["create:documents"]}>
                      <Button
                        variant="primary"
                        className="btn-sm px-3 text-nowrap shadow-sm d-inline-flex align-items-center justify-content-center"
                        style={{ height: "36px" }}
                        onClick={() => {
                          const params = new URLSearchParams();
                          if (filters.workOrder) params.set("workOrder", filters.workOrder);
                          navigate(`/tc-documents/upload${params.toString() ? `?${params.toString()}` : ""}`);
                        }}
                      >
                        + Add TC/Warranty Card
                      </Button>
                    </PermissionWrapper>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Row>
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="mb-0 table-hover align-middle" size="sm">
                  <thead className="table-light">
                    <tr>
                      <th className="text-nowrap" style={{ width: "60px" }}>Sr. No.</th>
                      <th className="text-nowrap" style={{ width: "130px" }}>Type</th>
                      <th className="text-nowrap" style={{ width: "130px" }}>WLIN</th>
                      <th style={{ minWidth: "260px" }}>Product Name</th>
                      <th className="text-nowrap" style={{ width: "170px" }}>WO / PO No.</th>
                      <th className="text-nowrap text-center" style={{ width: "70px" }}>UOM</th>
                      <th className="text-nowrap text-center" style={{ width: "70px" }}>QTY</th>
                      <th className="text-nowrap" style={{ width: "120px" }}>Submitted Date</th>
                      <th className="text-nowrap" style={{ width: "130px" }}>Status</th>
                      <th style={{ minWidth: "160px" }}>Documents</th>
                      <th className="text-center text-nowrap" style={{ width: "70px" }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="11" className="text-center py-5">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          ></div>
                        </td>
                      </tr>
                    ) : documents.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="text-center py-5 text-muted">
                          No TC/Warranty Cards found.
                        </td>
                      </tr>
                    ) : (
                      documents.map((doc, index) => {
                        const isWarranty =
                          doc.docCategory === "Warranty Card" ||
                          doc.documents?.some((d) => d.docType === "Warranty Card");

                        return (
                          <tr key={doc._id}>
                            <td className="text-center font-w500">
                              {(pagination.currentPage - 1) * filters.limit +
                                index +
                                1}
                            </td>

                            {/* Document Type Badge */}
                            <td>
                              {isWarranty ? (
                                <Badge bg="warning" className="text-dark font-w600 px-2 py-1">
                                  <i className="fas fa-shield-alt me-1"></i> Warranty Card
                                </Badge>
                              ) : (
                                <Badge bg="primary" className="font-w600 px-2 py-1">
                                  <i className="fas fa-certificate me-1"></i> TC
                                </Badge>
                              )}
                            </td>

                            {/* WLIN with compact wrap and +more */}
                            <td>
                              {(() => {
                                const boqWlins = (doc.boqItems || [])
                                  .filter(Boolean)
                                  .map((item) => item?.wlino)
                                  .filter(Boolean);
                                const rawWlins = doc.wlino
                                  ? doc.wlino.split(",").map((w) => w.trim()).filter(Boolean)
                                  : [];
                                const allWlins = boqWlins.length > 0 ? boqWlins : rawWlins;

                                if (allWlins.length === 0) return <span className="text-muted">—</span>;

                                if (allWlins.length <= 3) {
                                  return (
                                    <div className="d-flex flex-wrap gap-1 align-items-center" style={{ maxWidth: "130px" }}>
                                      {allWlins.map((wlin, i) => (
                                        <Badge key={i} bg="light" className="text-dark border font-w500 px-2 py-1">
                                          {wlin}
                                        </Badge>
                                      ))}
                                    </div>
                                  );
                                }

                                return (
                                  <div className="d-flex flex-wrap gap-1 align-items-center" style={{ maxWidth: "130px" }}>
                                    {allWlins.slice(0, 2).map((wlin, i) => (
                                      <Badge key={i} bg="light" className="text-dark border font-w500 px-2 py-1">
                                        {wlin}
                                      </Badge>
                                    ))}
                                    <Badge
                                      bg="info"
                                      className="text-white cursor-pointer px-2 py-1"
                                      title={allWlins.join(", ")}
                                      onClick={() => {
                                        setSelectedDoc(doc);
                                        setShowModal(true);
                                      }}
                                    >
                                      +{allWlins.length - 2}
                                    </Badge>
                                  </div>
                                );
                              })()}
                            </td>

                            {/* Product Name without internal scrollbars */}
                            <td>
                              {(() => {
                                const isMultiple = doc.productName?.includes(" / ");
                                if (!isMultiple) {
                                  return (
                                    <div>
                                      <div className="font-w600 text-dark">{doc.productName || "—"}</div>
                                      {doc.remarks && (
                                        <div className="small text-muted mt-1 text-truncate" style={{ maxWidth: "260px" }} title={doc.remarks}>
                                          <i className="far fa-comment-dots me-1"></i> {doc.remarks}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }

                                const items = doc.productName
                                  .split(" / ")
                                  .map((p) => p.trim())
                                  .filter(Boolean);

                                if (items.length <= 2) {
                                  return (
                                    <div>
                                      <div className="d-flex flex-column gap-1">
                                        {items.map((name, i) => (
                                          <div key={i} className="font-w600 text-dark small">
                                            • {name}
                                          </div>
                                        ))}
                                      </div>
                                      {doc.remarks && (
                                        <div className="small text-muted mt-1 text-truncate" style={{ maxWidth: "260px" }} title={doc.remarks}>
                                          <i className="far fa-comment-dots me-1"></i> {doc.remarks}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }

                                return (
                                  <div>
                                    <div className="d-flex flex-column gap-1">
                                      {items.slice(0, 2).map((name, i) => (
                                        <div key={i} className="font-w600 text-dark small">
                                          • {name}
                                        </div>
                                      ))}
                                      <span
                                        className="text-primary small font-w500 cursor-pointer"
                                        title={items.join("\n")}
                                        onClick={() => {
                                          setSelectedDoc(doc);
                                          setShowModal(true);
                                        }}
                                      >
                                        +{items.length - 2} more items...
                                      </span>
                                    </div>
                                    {doc.remarks && (
                                      <div className="small text-muted mt-1 text-truncate" style={{ maxWidth: "260px" }} title={doc.remarks}>
                                        <i className="far fa-comment-dots me-1"></i> {doc.remarks}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>

                            {/* WO / PO */}
                            <td>
                              <div>
                                <div className="font-w600 text-dark">{doc.workOrder?.woNo || "—"}</div>
                                {doc.purchaseOrder?.poNo && (
                                  <div className="text-muted small">
                                    PO: {doc.purchaseOrder.poNo}
                                  </div>
                                )}
                              </div>
                            </td>

                            <td className="text-center">{doc.quom || "—"}</td>
                            <td className="text-center font-w600">{doc.qty ?? "—"}</td>
                            <td>{formatDate(doc.submittedDate)}</td>

                            <td>
                              <Badge className="rounded-pill px-2 py-1" bg={getStatusBadge(doc.status)}>
                                {doc.status || "—"}
                              </Badge>
                              {doc.approvedBy?.name && (
                                <div className="small mt-1 text-muted">
                                  By: {doc.approvedBy.name}
                                </div>
                              )}
                            </td>

                            {/* Documents */}
                            <td>
                              <div className="d-flex flex-wrap gap-1 align-items-center">
                                {(() => {
                                  const allDocs =
                                    doc.documents && doc.documents.length > 0
                                      ? doc.documents
                                      : doc.document?.fileUrl
                                      ? [doc.document]
                                      : [];

                                  if (allDocs.length === 0) return <span className="text-muted small">—</span>;

                                  return (
                                    <>
                                      {allDocs.slice(0, 2).map((file, fIdx) => (
                                        <div
                                          key={fIdx}
                                          className="d-inline-flex align-items-center border rounded px-2 py-1 bg-light gap-1"
                                        >
                                          <span
                                            className="small text-truncate"
                                            style={{ maxWidth: "80px" }}
                                            title={file.fileName}
                                          >
                                            {file.fileName}
                                          </span>
                                          <a
                                            href={getFileUrl(file)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn btn-primary btn-xxs shadow-sm"
                                            title={`Preview ${file.fileName}`}
                                          >
                                            <i className="fas fa-eye"></i>
                                          </a>
                                          <a
                                            href={getFileUrl(file)}
                                            download={file.fileName}
                                            className="btn btn-success btn-xxs shadow-sm"
                                            title={`Download ${file.fileName}`}
                                          >
                                            <i className="fas fa-download"></i>
                                          </a>
                                        </div>
                                      ))}
                                      {allDocs.length > 2 && (
                                        <Button
                                          variant="outline-secondary"
                                          className="btn-xxs"
                                          onClick={() => {
                                            setSelectedDoc(doc);
                                            setShowModal(true);
                                          }}
                                          title="View all attached files"
                                        >
                                          +{allDocs.length - 2}
                                        </Button>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </td>

                            {/* Actions */}
                            <td className="text-center">
                              <Dropdown>
                                <Dropdown.Toggle
                                  as="div"
                                  className="btn btn-link c-pointer i-false p-0"
                                >
                                  {svgMenu}
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end" as={PortalMenu}>
                                  <PermissionWrapper user={user} required={["read:documents"]}>
                                    <Dropdown.Item
                                      onClick={() => {
                                        setSelectedDoc(doc);
                                        setShowModal(true);
                                      }}
                                    >
                                      <i className="fas fa-eye me-2 text-primary"></i> View Details
                                    </Dropdown.Item>
                                  </PermissionWrapper>

                                  <PermissionWrapper user={user} required={["edit:documents"]}>
                                    <Dropdown.Item
                                      as={Link}
                                      to={`/tc-documents/edit/${doc._id}${getListQueryString()}`}
                                    >
                                      <i className="fas fa-edit me-2 text-info"></i> Edit
                                    </Dropdown.Item>
                                  </PermissionWrapper>

                                  <Dropdown.Divider />
                                  <PermissionWrapper user={user} required={["delete:documents"]}>
                                    <Dropdown.Item
                                      onClick={() => handleDelete(doc._id)}
                                      className="text-danger"
                                    >
                                      <i className="fas fa-trash me-2"></i> Delete
                                    </Dropdown.Item>
                                  </PermissionWrapper>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <AppPagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              itemsLength={documents.length}
              limit={filters.limit}
              onPageChange={handlePageChange}
            />
          </Card>
        </Col>
      </Row>

      {/* Modal View Details */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>TC/Warranty Card Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDoc && (
            <div className="p-2">
              <Row className="mb-4">
                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 text-primary">Document Information</h5>
                    <Badge bg={getStatusBadge(selectedDoc.status)} className="rounded-pill px-3 py-2">
                      {selectedDoc.status || "—"}
                    </Badge>
                  </div>
                  <div className="table-responsive">
                    <Table bordered size="sm">
                      <tbody>
                        <tr>
                          <td className="bg-light font-w600" style={{ width: "30%" }}>
                            Document Type
                          </td>
                          <td>
                            {selectedDoc.docCategory === "Warranty Card" ||
                            selectedDoc.documents?.some((d) => d.docType === "Warranty Card") ? (
                              <Badge bg="warning" className="text-dark font-w600 px-2 py-1">
                                <i className="fas fa-shield-alt me-1"></i> Warranty Card
                              </Badge>
                            ) : (
                              <Badge bg="primary" className="font-w600 px-2 py-1">
                                <i className="fas fa-certificate me-1"></i> Test Certificate (TC)
                              </Badge>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="bg-light font-w600">
                            Work Order
                          </td>
                          <td>{selectedDoc.workOrder?.woNo || "—"}</td>
                        </tr>
                        {selectedDoc.purchaseOrder && (
                          <tr>
                            <td className="bg-light font-w600">
                              Purchase Order
                            </td>
                            <td>{selectedDoc.purchaseOrder.poNo}</td>
                          </tr>
                        )}
                        <tr>
                          <td className="bg-light font-w600">Product(s)</td>
                          <td>
                            {selectedDoc.productName?.includes(" / ") ? (
                              <ul className="ps-3 mb-0">
                                {selectedDoc.productName
                                  .split(" / ")
                                  .map((name, i) => (
                                    <li key={i}>{name}</li>
                                  ))}
                              </ul>
                            ) : (
                              selectedDoc.productName
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="bg-light font-w600">Total QTY</td>
                          <td>
                            {selectedDoc.qty} {selectedDoc.quom}
                          </td>
                        </tr>
                        <tr>
                          <td className="bg-light font-w600">Dates</td>
                          <td>
                            <div className="small text-muted">
                              Submitted: {formatDate(selectedDoc.submittedDate)}
                            </div>
                            {selectedDoc.approvedDate && (
                              <div className="small text-success">
                                Approved: {formatDate(selectedDoc.approvedDate)}
                              </div>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </Col>
              </Row>

              <h5 className="text-primary mb-3">Attached Files</h5>
              <div className="list-group list-group-flush border rounded mb-3">
                {(selectedDoc.documents?.length
                  ? selectedDoc.documents
                  : selectedDoc.document?.fileUrl
                  ? [selectedDoc.document]
                  : []
                ).map((file, index) => (
                  <div
                    key={`${file.fileUrl}-${index}`}
                    className="list-group-item d-flex justify-content-between align-items-center py-3"
                  >
                    <div className="d-flex align-items-center">
                      <div className="icon-box bg-primary-light text-primary me-3 rounded">
                        <i className="fas fa-file-pdf fa-lg"></i>
                      </div>
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <h6 className="mb-0 text-dark">{file.fileName}</h6>
                          <Badge
                            bg={file.docType === "Warranty Card" ? "warning" : "primary"}
                            className={file.docType === "Warranty Card" ? "text-dark" : ""}
                          >
                            {file.docType || selectedDoc.docCategory || "TC"}
                          </Badge>
                        </div>
                        {file.remarks && <small className="text-muted d-block">{file.remarks}</small>}
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <a
                        href={getFileUrl(file)}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-outline-primary btn-sm rounded-circle"
                        title="View"
                      >
                        <i className="fas fa-eye"></i>
                      </a>
                      <a
                        href={getFileUrl(file)}
                        download={file.fileName}
                        className="btn btn-outline-success btn-sm rounded-circle"
                        title="Download"
                      >
                        <i className="fas fa-download"></i>
                      </a>
                      <PermissionWrapper user={user} required={["edit:documents"]}>
                        <button
                          type="button"
                          onClick={() => handleDocumentDelete(selectedDoc._id, index)}
                          className="btn btn-outline-danger btn-sm rounded-circle"
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </PermissionWrapper>
                    </div>
                  </div>
                ))}

                {selectedDoc.signature?.fileUrl && (
                  <div className="list-group-item d-flex justify-content-between align-items-center py-3">
                    <div className="d-flex align-items-center">
                      <div className="icon-box bg-warning-light text-warning me-3 rounded">
                        <i className="fas fa-signature fa-lg"></i>
                      </div>
                      <div>
                        <h6 className="mb-0 text-dark">Signature</h6>
                        <small className="text-muted">
                          {selectedDoc.signature.fileName || "Captured via Canvas"}
                        </small>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <a
                        href={
                          selectedDoc.signature.fileUrl.startsWith("http")
                            ? selectedDoc.signature.fileUrl
                            : getFileUrl(selectedDoc.signature.fileUrl)
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-outline-warning btn-sm rounded-circle"
                      >
                        <i className="fas fa-eye"></i>
                      </a>
                    </div>
                  </div>
                )}

                {selectedDoc.stamp?.fileUrl && (
                  <div className="list-group-item d-flex justify-content-between align-items-center py-3">
                    <div className="d-flex align-items-center">
                      <div className="icon-box bg-info-light text-info me-3 rounded">
                        <i className="fas fa-stamp fa-lg"></i>
                      </div>
                      <div>
                        <h6 className="mb-0 text-dark">Stamp</h6>
                        <small className="text-muted">
                          {selectedDoc.stamp.fileName}
                        </small>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <a
                        href={
                          selectedDoc.stamp.fileUrl.startsWith("http")
                            ? selectedDoc.stamp.fileUrl
                            : getFileUrl(selectedDoc.stamp.fileUrl)
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-outline-info btn-sm rounded-circle"
                      >
                        <i className="fas fa-eye"></i>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {selectedDoc.remarks && (
                <div className="mt-3">
                  <h6 className="font-w600 text-dark">Remarks:</h6>
                  <p className="text-muted mb-0">{selectedDoc.remarks}</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <PermissionWrapper user={user} required={["edit:documents"]}>
            <Button
              variant="primary"
              as={Link}
              to={`/tc-documents/edit/${selectedDoc?._id}${getListQueryString()}`}
            >
              Edit TC/Warranty Card
            </Button>
          </PermissionWrapper>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default TCList;
